<template>
  <div
    v-show="!!data.length"
    :ref="setContentAreaEl"
    class="relative flex flex-auto flex-col overflow-auto rounded-ds-lg border border-border bg-card shadow-ds-sm select-none scrollbar-thin scrollbar-track-transparent scrollbar-thumb-muted-foreground/20"
  >
    <div
      :ref="setGridEl"
      data-pivot-grid="true"
      class="float-left grid min-w-fit [&>div:first-child]:rounded-tl-ds-lg"
      :style="gridStyles"
    >
      <PivotGridHeader
        :columns
        :cols-list
        :rows-list="layoutRowsList"
        :values-list
        @resize="handleResize"
        @reset="handleReset"
      />
      <template
        v-for="(item, index) in data"
        :key="getRowKey(item)"
      >
        <PivotGridRowModern
          v-if="layout === PivotTableLayout.MODERN"
          :item
          :columns
          :cols-list
          :rows-list
          :values-list
          :grid-ref="gridRef?.value ?? null"
          :row-index="index"
          @click:text="$emit('click:text', $event)"
          @click:value="(clickData, amount) => $emit('click:value', clickData, amount)"
        />
        <PivotGridRowClassic
          v-else
          :item
          :columns
          :cols-list
          :rows-list
          :values-list
          :label-start-depth="rowLabelStartDepth.get(item) ?? 0"
          :row-index="index"
          @click:text="$emit('click:text', $event)"
          @click:value="(clickData, amount) => $emit('click:value', clickData, amount)"
        />
      </template>
      <template v-if="isLazyIndicatorVisible">
        <DSSkeleton
          v-for="(_, index) in gridColumnKeys"
          :key="index"
          v-element-visibility="visible => $emit('sentinel-visible', visible)"
          class="h-8 rounded-none"
        />
      </template>
      <PivotGridFooter
        v-if="footer"
        :data="footer"
        :columns
        :cols-list
        :rows-list="layoutRowsList"
        :values-list
        :row="data.length"
        @click:value="(clickData, amount) => $emit('click:value', clickData, amount)"
      />
    </div>
  </div>
  <div
    v-if="isPreloading || isExpandingAll"
    class="absolute inset-0 z-30 flex items-center justify-center gap-3 bg-card/75"
  >
    <LoaderCircle class="size-5 animate-spin text-muted-foreground" />
    <span class="text-sm text-muted-foreground">{{ isExpandingAll ? 'Expanding rows…' : 'Loading data…' }}</span>
  </div>
  <div
    v-if="!isPreloading && !data.length"
    class="flex flex-auto flex-col items-center justify-center gap-2 py-10 text-center text-muted-foreground"
  >
    <DatabaseZap class="size-7" />
    <p class="text-sm">
      No data
    </p>
  </div>
</template>

<script setup lang="ts">
import { ComponentPublicInstance, Ref, computed, onMounted, onUnmounted, ref } from 'vue'
import { vElementVisibility } from '@vueuse/components'
import { DatabaseZap, LoaderCircle } from '@lucide/vue'

import {
  PivotTableData,
  PivotTableLayout,
} from '@types'

import { MIN_HEADER_WIDTH, MIN_RESIZED_WIDTH, TOTAL_MARKER_KEY } from '@/components/DS/patterns/Pivot/utils/const'
import { getColumnPathValues } from '@/components/DS/patterns/Pivot/utils/query'

import { DSSkeleton } from '@ds'

import PivotGridFooter from './PivotGridFooter.vue'
import PivotGridHeader from './PivotGridHeader.vue'
import PivotGridRowClassic from './PivotGridRowClassic.vue'
import PivotGridRowModern from './PivotGridRowModern.vue'

type Props = {
  /**
   * Layout and fields from last successful fetch—use `usePivotData`'s
   * `innerLayout`, `innerColsList`, `innerRowsList`, and `innerValuesList`
   * (not live config, which updates immediately and may misalign with stale data/columns).
   */
  layout: PivotTableLayout
  colsList?: string[]
  rowsList?: string[]
  valuesList?: string[]
  /** Fetched rows, already sorted if applicable. */
  data: PivotTableData[]
  footer?: PivotTableData
  /** Column groupings from the query, distinct from the fields' column selection. */
  columns?: PivotTableData[]

  isPreloading?: boolean
  isExpandingAll?: boolean
  isLazyIndicatorVisible?: boolean
  
  clickableValues?: boolean

  /** Caller-owned refs for grid/scroll DOM nodes. */
  gridRef?: Ref<HTMLDivElement | null>
  contentAreaRef?: Ref<HTMLDivElement | null>
}

type Emits = {
  'click:text': [data: string]
  'click:value': [data: Record<string, string | number | string[]>, amount: number]
  /** Lazy-load sentinel visibility; wire to `usePivotData().onElementVisibility`. */
  'sentinel-visible': [visible: boolean]
}

const {
  layout,
  colsList = [],
  rowsList = [],
  valuesList = [],
  data,
  footer,
  columns = [],
  isPreloading,
  isExpandingAll,
  isLazyIndicatorVisible,
  gridRef,
  contentAreaRef,
} = defineProps<Props>()
defineEmits<Emits>()

// For export: bind ref="pivotRef" on this component.
defineExpose({
  getExportElement: () => contentAreaRef?.value ?? null,
})

// Function refs, not `ref="gridRef"` — gridRef/contentAreaRef are Ref objects
// owned by the caller, not local template refs Vue's string-ref sugar would catch.
const setContentAreaEl = (el: Element | ComponentPublicInstance | null) => {
  // Filling .value isn't reassigning the prop — it's the caller's own ref.
  // eslint-disable-next-line vue/no-mutating-props
  if (contentAreaRef) contentAreaRef.value = el as HTMLDivElement | null
}
const setGridEl = (el: Element | ComponentPublicInstance | null) => {
  // eslint-disable-next-line vue/no-mutating-props
  if (gridRef) gridRef.value = el as HTMLDivElement | null
}

// Row hover: every cell in a row already shares one `data-row` (set only while the cell is
// actually rendered/visible — see `PivotGridRowModern`/`Classic`), so one delegated listener here
// can paint the whole row via `data-row-hovered` (styled below) — no per-cell listener, and no
// reactive state, matching `usePivotSelection`'s own direct-DOM approach for the same perf reason
// (`classList`/`style` mutation beats a reactive class on every cell at this cardinality). Owned
// here, not by `usePivotSelection` or the widget, since it's a pure grid-internal visual effect
// with nothing to expose outside the grid (unlike selection's summary bar).
let hoveredRow: string | null = null
const clearHoveredRow = () => {
  if (hoveredRow === null) return
  gridRef?.value?.querySelectorAll(`[data-row="${hoveredRow}"]`).forEach(el => el.removeAttribute('data-row-hovered'))
  hoveredRow = null
}
const handleRowMouseOver = (event: MouseEvent) => {
  const cell = (event.target as HTMLElement).closest('[data-row]') as HTMLElement | null
  const row = cell?.dataset.row
  // Negative rows are header levels (see `PivotGridHeader`'s `row` — counts up to -1 above the
  // body) — headers aren't part of "row hover".
  if (row === undefined || row === hoveredRow || Number(row) < 0) return
  clearHoveredRow()
  hoveredRow = row
  gridRef?.value?.querySelectorAll(`[data-row="${row}"]`).forEach(el => el.setAttribute('data-row-hovered', 'true'))
}

onMounted(() => {
  gridRef?.value?.addEventListener('mouseover', handleRowMouseOver)
  gridRef?.value?.addEventListener('mouseleave', clearHoveredRow)
})
onUnmounted(() => {
  gridRef?.value?.removeEventListener('mouseover', handleRowMouseOver)
  gridRef?.value?.removeEventListener('mouseleave', clearHoveredRow)
})

// Modern nests every row level into one column; Classic gives each its own sibling column.
const layoutRowsList = computed(() => layout === PivotTableLayout.MODERN ? rowsList.slice(0, 1) : rowsList)

const getRowKey = (item: PivotTableData) => {
  const key = rowsList.map(row => item[row] ?? '__null__').join('|') || String(data.indexOf(item))
  // total markers need unique keys to avoid v-for collisions
  return item[TOTAL_MARKER_KEY] ? `${key}__total` : key
}

const getItemDepth = (item: PivotTableData) =>
  rowsList.map(row => item[row]).filter(Boolean).length ?? 0

// Classic: first row at a new depth shows its full chain; siblings collapse repeated prefixes.
const rowLabelStartDepth = computed(() => {
  const result = new Map<PivotTableData, number>()
  if (layout !== PivotTableLayout.CLASSIC) return result
  const rowsFields = rowsList || []
  const lastRowAtDepth = new Map<number, PivotTableData>()
  for (const item of data) {
    if (item[TOTAL_MARKER_KEY]) continue
    const depth = getItemDepth(item)
    const priorSibling = lastRowAtDepth.get(depth)
    let startDepth = 0
    if (priorSibling) {
      for (; startDepth < depth; startDepth++) {
        if (item[rowsFields[startDepth]] !== priorSibling[rowsFields[startDepth]]) break
      }
    }
    result.set(item, startDepth)
    lastRowAtDepth.set(depth, item)
  }
  return result
})

const gridColumnKeys = computed(() => {
  const keys = layoutRowsList.value?.map(row => [row]) || []
  const columnKeys = columns.map(column => getColumnPathValues(column) as string[])
  if (valuesList.length > 1) {
    for (const column of columns || []) {
      const key = getColumnPathValues(column) as string[]
      for (const value of valuesList) {
        keys.push([...key, value])
      }
    }
  } else if (valuesList.length !== 0) {
    for (const column of columnKeys) {
      keys.push(column)
    }
  }
  for (const value of valuesList) {
    keys.push([value])
  }
  return keys
})

// Column widths are ephemeral UI state, owned locally and never persisted.
const columnsWidth = ref<Record<string, number | string>>({})

const gridStyles = computed(() => ({
  gridTemplateColumns: gridColumnKeys.value.map(key => key.join('_'))
    ?.map(key => {
      let value = `minmax(${MIN_HEADER_WIDTH}px, max-content)`
      const size = columnsWidth.value?.[key]
      if (typeof size === 'string') {
        value = size
      } else if (size) {
        value = `${size < MIN_RESIZED_WIDTH ? MIN_RESIZED_WIDTH : size}px`
      }
      return `${value} `
    })
    .join(' '),
}))

const handleResize = (key: string[], width: number) => {
  columnsWidth.value[key.join('_')] = width
}

const handleReset = (key: string[]) => {
  columnsWidth.value[key.join('_')] = 'max-content'
}
</script>

<style>
[data-header-cell] {
  @apply border-r border-b border-border;
}
[data-cell] {
  @apply border-r border-b border-border/30;
}
[data-selected] {
  @apply shadow-primary data-copy:shadow-border;
}
[data-selected]:not([data-copy]) {
  @apply outline-none;
  @apply bg-[#dddbf5]! dark:bg-[#29264e]!;
  @apply border-transparent!;
}
[data-selected='t'] {
  @apply shadow-[inset_0_2px_0_0];
}
[data-selected='tr'] {
  @apply shadow-[inset_-2px_2px_0_0];
}
[data-selected='r'] {
  @apply shadow-[inset_-2px_0_0_0];
}
[data-selected='br'] {
  @apply shadow-[inset_-2px_-2px_0_0];
}
[data-selected='b'] {
  @apply shadow-[inset_0_-2px_0_0];
}
[data-selected='bl'] {
  @apply shadow-[inset_2px_-2px_0_0];
}
[data-selected='l'] {
  @apply shadow-[inset_2px_0_0_0];
}
[data-selected='tl'] {
  @apply shadow-[inset_2px_2px_0_0];
}
[data-selected='tb'] {
  @apply shadow-[inset_0_2px_0_0,inset_0_-2px_0_0];
}
[data-selected='lr'] {
  @apply shadow-[inset_2px_0_0_0,inset_-2px_0_0_0];
}
[data-selected='tlr'] {
  @apply shadow-[inset_2px_2px_0_0,inset_-2px_0_0_0];
}
[data-selected='blr'] {
  @apply shadow-[inset_2px_-2px_0_0,inset_-2px_0_0_0];
}
[data-selected='tbr'] {
  @apply shadow-[inset_-2px_-2px_0_0,inset_0_2px_0_0];
}
[data-selected='tbl'] {
  @apply shadow-[inset_2px_-2px_0_0,inset_0_2px_0_0];
}
[data-selected='tblr'] {
  @apply shadow-[inset_0_2px_0_0,inset_0_-2px_0_0,inset_2px_0_0_0,inset_-2px_0_0_0];
}
</style>
